'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { trackEmerald } from './analytics';
import { FlowProvider, useFlowState } from './FlowProvider';
import { IntroOrb } from './scenes/IntroOrb';
import { InsightsScene } from './scenes/InsightsScene';
import { NameScene } from './scenes/NameScene';
import { SkyScene } from './scenes/SkyScene';
import { SphereHandleContext } from './sphereContext';
import { SphereLoadingScreen } from './SphereLoadingScreen';
import {
  CanvasHost,
  Stage,
  Void,
  Wordmark,
  WordmarkLogo,
  WordmarkName,
} from './styles';
import { geist, instrumentSerif } from 'utils/fonts';
import type { SphereHandle } from './types';

// Via the SphereCanvasClient wrapper because next/dynamic does not forward
// React's `ref` — the wrapper re-attaches the handle from a plain prop.
const SphereCanvas = dynamic(
  () =>
    import('./SphereCanvasClient').then((mod) => ({
      default: mod.SphereCanvasClient,
    })),
  {
    ssr: false,
    loading: () => <SphereLoadingScreen />,
  },
);

const EmeraldSphereBody = () => {
  const { phase, revealed, perched } = useFlowState();
  const sphereRef = useRef<SphereHandle | null>(null);
  const orbWrapRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;
  const searchParams = useSearchParams();

  // Page-scoped scroll lock (:global(html.emerald-lock) in styles.module.css).
  // The cleanup is load-bearing: route CSS is NOT unloaded on a client-side nav
  // away, so without it the lock leaks onto the next page.
  useEffect(() => {
    document.documentElement.classList.add('emerald-lock');
    return () => document.documentElement.classList.remove('emerald-lock');
  }, []);

  // A `?name=`/`?shared=1` link restarts the flow like a fresh visit; the param
  // exists only to tell a shared-link open apart from a direct one.
  useEffect(() => {
    if (searchParams.get('name') || searchParams.get('shared')) {
      trackEmerald('emerald_shared_link_opened');
    } else {
      trackEmerald('emerald_enter');
    }
  }, []);

  // The dynamic import can resolve after first paint, so seed reduced-motion at
  // attach time rather than relying on the effect below alone.
  const attachSphereRef = useCallback((instance: SphereHandle | null) => {
    sphereRef.current = instance;
    instance?.setReducedMotion(reducedMotionRef.current);
  }, []);

  useEffect(() => {
    sphereRef.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion]);

  useEffect(
    () => () => {
      sphereRef.current?.dispose();
    },
    [],
  );

  return (
    <SphereHandleContext.Provider value={sphereRef}>
      <Void />
      {/* LOAD-BEARING: these font-variable classes must sit on a CLIENT-rendered
          element. Referenced only from app/layout.tsx (a Server Component),
          Turbopack tree-shakes the @font-face away and the vars come out empty —
          text silently falls back to Graphik. Same trick as app/providers.tsx. */}
      <Stage className={`${geist.variable} ${instrumentSerif.variable}`}>
        {/* Keep this a plain <a>, NOT next/link. This page suppresses GTM/gtag at
            load, so leaving it must be a hard nav for app/layout.tsx to re-run and
            re-initialise analytics — a SPA transition leaves them off for the rest
            of the session. Same for the noscript fallback link in the page. */}
        <Wordmark href="/ring/buy" aria-label="Ultrahuman — home">
          <WordmarkLogo
            src="/emerald-sphere/uh-wordmark.png"
            alt="Ultrahuman"
          />
          <WordmarkName>Emerald</WordmarkName>
        </Wordmark>

        {/* Nested inside OrbWrap so the canvas inherits its size/position/perch
            transition instead of filling the whole Stage. */}
        <IntroOrb orbWrapRef={orbWrapRef} revealed={revealed} perched={perched}>
          <CanvasHost>
            <SphereCanvas handleRef={attachSphereRef} initialMood="default" />
          </CanvasHost>
        </IntroOrb>
        <NameScene active={phase === 'name'} />
        <SkyScene active={phase === 'sky'} />
        <InsightsScene
          active={phase === 'insights'}
          orbWrapRef={orbWrapRef}
          reducedMotion={reducedMotion}
        />
      </Stage>
    </SphereHandleContext.Provider>
  );
};

export const EmeraldSphereExperience = () => (
  <FlowProvider>
    <EmeraldSphereBody />
  </FlowProvider>
);
